/**
 * ImmigrationPage.js
 *
 * /fly/immigration — Unclaimed Declarations, the party's guessing game, on guests' phones.
 * Every card on the wall is a declaration (two truths and a lie, no name). For each one, the guest files a report:
 * who declared it, and which statement is the lie. Cards are a swipeable deck; the rail at the bottom jumps to any
 * card. A printed card's QR (/fly/immigration?card=07) or the Scan button brings that card to the front.
 *
 * They type who they think declared it; suggestions appear as they type, and only a guest-list name counts.
 * Guests sign in like check-in (PNR + last name, same cookie). Picks are saved straight away and, if the network
 * drops, kept on the phone and retried until they're saved. The game is checked every 20 seconds.
 * Phone-first: 100dvh layout, safe areas, 44px tap targets, 16px text, transform/opacity animations only.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, CircleAlert, Pin, ScanLine, X } from "lucide-react";
import toast from "react-hot-toast";
import QrScanner from "./components/QrScanner";
import { CARD_INK, CARD_PAPER, CARD_RED, CardBand, cardLabel } from "./components/DeclarationCard";
import { checkIn, fileReport, getCheckInSession, getImmigration, PNR_PATTERN, setPin } from "./helpers/flyApi";
import { flyPath, formatHms, formatTime, isTrial, useNoIndex, useTimeLeft } from "./helpers/flyPage";

const MUTED = "#5B6577";
const LINE = "#D9D2C3";
const BACKDROP = "#E9E4DA";
const YELLOW = "#F2C230";
const OK = "#0D6B4F";
const MONO = "'JetBrains Mono', monospace";
const SANS = "'Space Grotesk', system-ui, sans-serif";

const POLL_MS = 20000;
const RETRY_MS = 4000;
const ADVANCE_MS = 1000;

const FILTERS = [
    { id: "all",    label: "All" },
    { id: "todo",   label: "To do" },
    { id: "pinned", label: "Pinned" },
];

const prefersReducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Animations and the few things Tailwind can't do (scroll-snap deck, safe areas, dvh), scoped to this page
const STYLES = `
.yj-imm{height:100vh;height:100dvh;display:flex;flex-direction:column;overflow:hidden;background:${BACKDROP};color:${CARD_INK};font-family:${SANS};text-align:left;font-size:16px}
.yj-imm button,.yj-imm a,.yj-imm label{touch-action:manipulation;-webkit-tap-highlight-color:transparent}
.yj-imm .imm-header{padding:calc(env(safe-area-inset-top,0px) + 10px) max(16px,env(safe-area-inset-right,0px)) 8px max(16px,env(safe-area-inset-left,0px))}
.yj-imm .imm-deck{flex:1;min-height:0;display:flex;gap:12px;overflow-x:auto;overflow-y:hidden;scroll-snap-type:x mandatory;touch-action:pan-x;overscroll-behavior-x:contain;
  padding:8px calc((100vw - var(--card-w)) / 2);scrollbar-width:none;--card-w:min(86vw,420px)}
.yj-imm .imm-deck::-webkit-scrollbar{display:none}
.yj-imm .imm-card{flex:0 0 var(--card-w);height:100%;scroll-snap-align:center;scroll-snap-stop:always;display:flex;flex-direction:column;position:relative;
  background:${CARD_PAPER};border-radius:18px;overflow:hidden;box-shadow:0 10px 26px rgba(27,42,65,.16);transition:box-shadow .3s}
.yj-imm .imm-statements{flex:1;min-height:0;overflow-y:auto;touch-action:pan-y;overscroll-behavior:contain}
.yj-imm .imm-rail{padding:6px max(12px,env(safe-area-inset-right,0px)) calc(env(safe-area-inset-bottom,0px) + 8px) max(12px,env(safe-area-inset-left,0px));background:${CARD_PAPER};border-top:1px solid ${LINE}}
.yj-imm .imm-chips{display:flex;gap:8px;overflow-x:auto;scrollbar-width:none;touch-action:pan-x;padding:4px 2px}
.yj-imm .imm-chips::-webkit-scrollbar{display:none}
@keyframes imm-fly{0%{transform:scale(.82) rotate(-5deg);opacity:0}60%{transform:scale(1.03) rotate(1deg);opacity:1}100%{transform:none;opacity:1}}
@keyframes imm-ring{0%,100%{box-shadow:0 0 0 0 ${YELLOW}00,0 10px 26px rgba(27,42,65,.16)}30%{box-shadow:0 0 0 5px ${YELLOW},0 10px 26px rgba(27,42,65,.16)}}
@keyframes imm-stamp{0%{transform:translate(-50%,-50%) rotate(-12deg) scale(1.6);opacity:0}55%{transform:translate(-50%,-50%) rotate(-12deg) scale(.94);opacity:1}75%{transform:translate(-50%,-50%) rotate(-12deg) scale(1.04)}100%{transform:translate(-50%,-50%) rotate(-12deg) scale(1);opacity:1}}
@keyframes imm-fade{from{opacity:0}to{opacity:1}}
.yj-imm .imm-fly{animation:imm-fly .6s cubic-bezier(.2,.8,.2,1) both,imm-ring 1.4s ease .2s}
.yj-imm .imm-stamp{position:absolute;left:50%;top:46%;transform:translate(-50%,-50%) rotate(-12deg);pointer-events:none;z-index:3;
  border:4px solid currentColor;border-radius:10px;padding:4px 14px;font-weight:800;letter-spacing:.16em;font-size:clamp(18px,6.4vw,26px);background:transparent;opacity:.78;white-space:nowrap}
.yj-imm .imm-stamp.long{font-size:clamp(14px,4.4vw,19px);letter-spacing:.12em;top:64%}
.yj-imm .imm-stamp.thud{animation:imm-stamp .45s cubic-bezier(.3,1.4,.5,1) both}
@media (prefers-reduced-motion:reduce){
  .yj-imm .imm-fly,.yj-imm .imm-stamp.thud{animation:imm-fade .25s ease both}
}
@media (orientation:landscape) and (max-height:500px){
  .yj-imm .imm-header{padding-top:calc(env(safe-area-inset-top,0px) + 4px);padding-bottom:4px}
  .yj-imm .imm-deck{--card-w:min(70vw,560px);padding-top:4px;padding-bottom:4px}
  .yj-imm .imm-rail{display:flex;align-items:center;gap:10px;padding-top:4px}
}
`;

// ---------------- Picks waiting to be saved: kept on the phone so none is ever lost ---------------- //

const pendingKey = (pnr) => `${isTrial() ? "yj030-trial" : "yj030"}-immigration-pending-${pnr}`;

const readPending = (pnr) => {
    try {
        return JSON.parse(localStorage.getItem(pendingKey(pnr)) || "{}");
    } catch {
        return {};
    }
};

const writePending = (pnr, pending) => {
    try {
        if (Object.keys(pending).length) localStorage.setItem(pendingKey(pnr), JSON.stringify(pending));
        else localStorage.removeItem(pendingKey(pnr));
    } catch {
        // Private mode: they stay in memory and are still retried while the page is open
    }
};

// ---------------- Signing in ---------------- //

/**
 * PNR and last name, like check-in. On success the page carries on, at the same URL (so ?card=07 is kept).
 */
const SignIn = ({ onSignedIn }) => {
    const [pnr, setPnr] = useState("");
    const [lastName, setLastName] = useState("");
    const [error, setError] = useState("");
    const [checking, setChecking] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const cleanPnr = pnr.trim().toUpperCase();
        if (!PNR_PATTERN.test(cleanPnr) || !lastName.trim()) {
            setError("Enter your 6-character PNR and your last name, as shown on your invite.");
            return;
        }
        setError("");
        setChecking(true);
        try {
            const booking = await checkIn(cleanPnr, lastName);
            onSignedIn(booking.pass.pnr);
        } catch (err) {
            if (err.status === 403) {
                setError(<>Sorry, your ticket isn't confirmed yet. Please visit <Link to={flyPath(`/fly/${cleanPnr}`)} className="font-bold underline">isayyashtothat.com{flyPath(`/fly/${cleanPnr}`)}</Link> to confirm your ticket.</>);
            } else {
                setError(err.status === 404 || err.status === 429 ? err.message : "Something went wrong. Please try again.");
            }
            setChecking(false);
        }
    };

    const input = "w-full rounded-xl border px-4 py-3 bg-white text-base focus:outline-none focus:ring-2";
    return (
        <div className="flex-1 overflow-y-auto px-5 py-6">
            <form onSubmit={handleSubmit} className="mx-auto w-full max-w-md flex flex-col gap-4 rounded-2xl p-5" style={{ background: CARD_PAPER }}>
                <div>
                    <h1 className="text-2xl font-bold">Show your passport</h1>
                    <p style={{ color: MUTED }}>Sign in with your PNR and last name to file your reports.</p>
                </div>
                <label className="flex flex-col gap-1.5">
                    <span className="text-[11px] font-bold tracking-[0.2em]" style={{ color: MUTED }}>PNR / BOOKING CODE</span>
                    <input value={pnr} onChange={(e) => setPnr(e.target.value.toUpperCase())} maxLength={6} autoCapitalize="characters" autoCorrect="off" autoComplete="off" spellCheck={false}
                        placeholder="e.g. CF38US" className={`${input} tracking-[0.35em] font-bold`} style={{ borderColor: LINE, fontFamily: MONO, "--tw-ring-color": CARD_INK }} />
                </label>
                <label className="flex flex-col gap-1.5">
                    <span className="text-[11px] font-bold tracking-[0.2em]" style={{ color: MUTED }}>LAST NAME</span>
                    <input value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={100} autoComplete="family-name"
                        placeholder="As on your boarding pass" className={input} style={{ borderColor: LINE, "--tw-ring-color": CARD_INK }} />
                </label>
                {error && <p role="alert" className="flex gap-2 text-sm" style={{ color: CARD_RED }}><CircleAlert size={18} className="shrink-0" aria-hidden="true" /><span>{error}</span></p>}
                <button type="submit" disabled={checking} className="min-h-[52px] rounded-full text-sm font-bold tracking-[0.2em] disabled:opacity-60" style={{ background: CARD_INK, color: CARD_PAPER }}>
                    {checking ? "CHECKING…" : "SHOW PASSPORT"}
                </button>
            </form>
        </div>
    );
};

// ---------------- Naming the passenger ---------------- //


// Names compare ignoring case and extra spaces
const tidyName = (name) => name.normalize("NFKC").trim().split(/\s+/).join(" ").toLowerCase();

/**
 * Who declared it: a text box with a dropdown. Tapping it lists the people still left (everyone this guest hasn't
 * named on another card yet); typing narrows it, and also finds people already named elsewhere ("on No. 05"), to move
 * them. Only a name on the guest list counts: tapping a suggestion, or typing one exactly.
 * @param {Array} passengers    -   Attending guests (but the player): [{ pnr, name, city, colour }]
 * @param {Object} usedOn       -   { [pnr]: card number } for everyone this guest named on another card
 * @param {Object} draft        -   { suspectPnr, suspectText }
 * @param {Function} onChange   -   Called with { suspectPnr (or null), suspectText }
 */
const PassengerField = ({ number, passengers, passengersByPnr, usedOn, draft, onChange }) => {
    const [focused, setFocused] = useState(false);
    const chosen = draft?.suspectPnr ? passengersByPnr[draft.suspectPnr] : null;
    const text = draft?.suspectText ?? chosen?.name ?? "";
    const query = tidyName(text);
    const left = useMemo(() => passengers.filter((p) => !usedOn[p.pnr]), [passengers, usedOn]);

    const suggestions = useMemo(() => {
        // Nothing typed (or the chosen name, untouched): everyone still left, to pick or change to
        if (!query || (chosen && tidyName(chosen.name) === query)) return left.filter((p) => p !== chosen);
        // Names where a word starts with what's typed come first, then names containing it anywhere; people still left first
        const matches = (p) => tidyName(p.name).split(" ").some((word) => word.startsWith(query)) || tidyName(p.name).startsWith(query);
        const starts = passengers.filter(matches);
        const contains = passengers.filter((p) => !matches(p) && tidyName(p.name).includes(query));
        return [...starts, ...contains].sort((a, b) => (usedOn[a.pnr] ? 1 : 0) - (usedOn[b.pnr] ? 1 : 0));
    }, [passengers, left, usedOn, query, chosen]);

    const type = (value) => {
        // A name typed out exactly counts as choosing it
        const exact = passengers.find((p) => tidyName(p.name) === tidyName(value));
        onChange({ suspectPnr: exact ? exact.pnr : null, suspectText: value });
    };

    const choose = (passenger) => {
        onChange({ suspectPnr: passenger.pnr, suspectText: passenger.name });
        setFocused(false);
        document.activeElement?.blur();
    };

    const unknown = !!query && !chosen && !focused;
    return (
        <div className="relative">
            <label className="block rounded-xl border-2 border-dashed px-4 py-2" style={{ borderColor: chosen ? CARD_INK : unknown ? CARD_RED : LINE, background: "#FFFFFF" }}>
                <span className="block text-[10px] font-bold tracking-[0.22em]" style={{ color: MUTED }}>PASSENGER</span>
                <span className="flex items-center gap-2">
                    {chosen && <span className="h-3 w-3 rounded-full shrink-0" style={{ background: chosen.colour }} aria-hidden="true" />}
                    <input
                        type="text"
                        value={text}
                        onChange={(e) => type(e.target.value)}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setTimeout(() => setFocused(false), 150)}
                        onKeyDown={(e) => { if (e.key === "Enter" && suggestions[0]) { e.preventDefault(); choose(suggestions[0]); } }}
                        placeholder="Type who you think declared this"
                        aria-label={`Who declared No. ${cardLabel(number)}?`}
                        aria-autocomplete="list"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        maxLength={60}
                        className="flex-1 min-w-0 bg-transparent text-base font-bold py-1 focus:outline-none placeholder:font-normal"
                        style={{ fontSize: 16 }}
                    />
                    {chosen && <span className="text-sm shrink-0" style={{ color: MUTED }}>{chosen.city}</span>}
                </span>
            </label>
            {unknown && <p className="pt-1 text-sm" style={{ color: CARD_RED }}>Choose a name from the guest list.</p>}
            {focused && (suggestions.length > 0 || !query) && (
                <ul className="absolute left-0 right-0 top-full mt-1 z-20 rounded-xl overflow-y-auto overscroll-contain" role="listbox"
                    style={{ background: "#FFFFFF", boxShadow: "0 12px 28px rgba(27,42,65,.22)", border: `1px solid ${LINE}`, maxHeight: "min(264px, 42dvh)", touchAction: "pan-y" }}>
                    {!query && (
                        <li className="sticky top-0 px-4 py-2 text-[10px] font-bold tracking-[0.2em]" style={{ background: "#FFFFFF", color: MUTED, borderBottom: `1px solid ${LINE}` }}>
                            {left.length ? `${left.length} ${left.length === 1 ? "PERSON" : "PEOPLE"} LEFT TO NAME` : "YOU'VE NAMED EVERYONE · TYPE TO MOVE SOMEONE"}
                        </li>
                    )}
                    {suggestions.map((passenger) => (
                        <li key={passenger.pnr} role="option" aria-selected={false}>
                            {/* onMouseDown keeps the box from losing focus before the tap lands */}
                            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => choose(passenger)} className="w-full min-h-[44px] flex items-center gap-3 px-4 text-left active:bg-black/5">
                                <span className="h-3 w-3 rounded-full shrink-0" style={{ background: passenger.colour }} aria-hidden="true" />
                                <span className="flex-1 min-w-0 text-base"><b className="font-semibold">{passenger.name}</b> <span style={{ color: MUTED }}>· {passenger.city}</span></span>
                                {usedOn[passenger.pnr] && <span className="text-[11px] font-bold shrink-0" style={{ color: MUTED }}>on No. {cardLabel(usedOn[passenger.pnr])}</span>}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// ---------------- One card ---------------- //

/**
 * A stamp across the card: CLEARED (filed), DIPLOMATIC IMMUNITY (their own)
 */
const Stamp = ({ text, colour, thud, long = false }) => (
    <div className={`imm-stamp ${long ? "long" : ""} ${thud ? "thud" : ""}`} style={{ color: colour }} aria-hidden="true">{text}</div>
);

const Card = ({
    card, phase, own, report, pending, pinned, draft, editing, passengers, passengersByPnr, usedOn, result, peopleRevealed, fly, stampThud,
    onPassenger, onPickLie, onFile, onAmend, onTogglePin, cardRef,
}) => {
    const open = phase === "open";
    const filed = !!(report || pending);
    const picking = open && !own && (!filed || editing);
    const shown = picking ? draft : (pending || report);
    const suspect = shown?.suspectPnr ? passengersByPnr[shown.suspectPnr] : null;
    // Once the people are revealed, only the lie is still to pick
    const ready = picking && (peopleRevealed || draft?.suspectPnr) && draft?.lie !== undefined && draft?.lie !== null;
    // The lie is shown once the TV reaches the card (who declared it can be shown before that, on the phones)
    const lieShown = result?.lie !== undefined;

    return (
        <article ref={cardRef} className={`imm-card ${fly ? "imm-fly" : ""}`} aria-label={`Declaration No. ${cardLabel(card.number)}`}>
            <CardBand number={card.number} compact>
                {!own && (
                    <button type="button" onClick={onTogglePin} aria-pressed={pinned} aria-label={pinned ? "Unpin" : "Pin"} className="h-11 w-11 rounded-full inline-flex items-center justify-center" style={{ background: pinned ? YELLOW : "rgba(251,247,239,.14)", color: pinned ? CARD_INK : CARD_PAPER }}>
                        <Pin size={18} fill={pinned ? CARD_INK : "none"} aria-hidden="true" />
                    </button>
                )}
            </CardBand>

            <div className="flex-1 min-h-0 flex flex-col gap-3 p-4">
                {/* Who declared it: a passport-style field */}
                {own ? (
                    <p className="rounded-xl px-4 py-3 text-sm" style={{ background: BACKDROP }}>That's your declaration. Nice try.</p>
                ) : result?.passenger ? (
                    <div className="rounded-xl border-2 px-4 py-2.5" style={{ borderColor: result.rightPassenger ? OK : CARD_RED }}>
                        <span className="block text-[10px] font-bold tracking-[0.22em]" style={{ color: MUTED }}>DECLARED BY</span>
                        <span className="flex items-center gap-2 text-base font-bold">
                            <span className="h-3 w-3 rounded-full" style={{ background: result.passenger.colour }} aria-hidden="true" />
                            {result.passenger.name} · {result.passenger.city}
                        </span>
                        <span className="block text-sm" style={{ color: result.rightPassenger ? OK : CARD_RED }}>
                            {result.rightPassenger ? "✓ You got it" : suspect ? `✗ You said ${suspect.name}` : "✗ No passenger picked"}
                        </span>
                    </div>
                ) : picking ? (
                    <PassengerField number={card.number} passengers={passengers} passengersByPnr={passengersByPnr} usedOn={usedOn} draft={draft} onChange={onPassenger} />
                ) : (
                    <div className="min-h-[56px] w-full rounded-xl border-2 border-dashed px-4 py-2" style={{ borderColor: suspect ? CARD_INK : LINE, background: "#FFFFFF" }}>
                        <span className="block text-[10px] font-bold tracking-[0.22em]" style={{ color: MUTED }}>PASSENGER</span>
                        {suspect ? (
                            <span className="flex items-center gap-2 text-base font-bold">
                                <span className="h-3 w-3 rounded-full shrink-0" style={{ background: suspect.colour }} aria-hidden="true" />
                                <span className="truncate">{suspect.name} · {suspect.city}</span>
                            </span>
                        ) : (
                            <span className="block text-base" style={{ color: MUTED }}>Not filed</span>
                        )}
                        {result && <span className="block text-xs" style={{ color: MUTED }}>Who declared it? Next on the TV…</span>}
                    </div>
                )}

                {/* The three statements; the area scrolls if they're long, so the button below stays put */}
                <ol className="imm-statements flex flex-col gap-2">
                    {card.statements.map((statement, index) => {
                        const pickedLie = shown?.lie === index;
                        const isLie = lieShown && result.lie === index;
                        // Once the lie is revealed, only the real lie is struck through; a wrong pick is marked instead
                        const wrongPick = lieShown && pickedLie && !isLie;
                        return (
                            <li key={index}>
                                <label className={`flex items-start gap-3 rounded-xl px-3 py-2.5 ${picking ? "cursor-pointer" : ""}`}
                                    style={{ background: isLie ? `${CARD_RED}10` : pickedLie && !lieShown ? `${CARD_RED}14` : "#FFFFFF", border: `1px ${wrongPick ? "dashed" : "solid"} ${isLie ? CARD_RED : wrongPick ? MUTED : LINE}` }}>
                                    <span className="flex-1 flex flex-col">
                                        <span className="text-base leading-snug" style={{ textDecoration: isLie || (pickedLie && !lieShown) ? "line-through" : "none", textDecorationColor: CARD_RED, color: isLie || (pickedLie && !lieShown) ? CARD_RED : CARD_INK }}>
                                            {statement}
                                        </span>
                                        {/* Outside the struck-through text, so the label itself isn't crossed out */}
                                        {isLie && <b className="text-[11px] tracking-[0.18em]" style={{ color: CARD_RED }}>THE LIE {own ? "" : result.rightLie ? "· ✓ YOU GOT IT" : "· ✗ YOU MISSED IT"}</b>}
                                        {wrongPick && <b className="text-[11px] tracking-[0.18em]" style={{ color: MUTED }}>✗ YOUR PICK</b>}
                                    </span>
                                    {!own && (
                                        <span className="flex flex-col items-center gap-0.5 shrink-0 min-w-[44px]">
                                            <input
                                                type="checkbox"
                                                checked={pickedLie}
                                                disabled={!picking}
                                                onChange={() => onPickLie(index)}
                                                className="h-7 w-7"
                                                style={{ accentColor: CARD_RED }}
                                                aria-label={`Statement ${index + 1} is the lie`}
                                            />
                                            <span className="text-[10px] font-bold tracking-[0.12em]" style={{ color: pickedLie ? CARD_RED : MUTED }}>LIE?</span>
                                        </span>
                                    )}
                                </label>
                            </li>
                        );
                    })}
                </ol>

                {/* In thumb reach */}
                {picking && (
                    <button type="button" onClick={onFile} disabled={!ready} className="min-h-[56px] w-full rounded-full text-base font-bold tracking-[0.2em] transition-opacity disabled:opacity-40" style={{ background: CARD_RED, color: CARD_PAPER }}>
                        FILE REPORT
                    </button>
                )}
                {open && !own && filed && !editing && (
                    <button type="button" onClick={onAmend} className="min-h-[44px] self-center px-4 text-sm font-bold tracking-[0.14em] underline underline-offset-4">
                        AMEND REPORT
                    </button>
                )}
            </div>

            {own && <Stamp text="DIPLOMATIC IMMUNITY" colour={CARD_INK} long />}
            {!own && filed && !editing && !lieShown && <Stamp text={pending ? "SAVING…" : "CLEARED"} colour={pending ? MUTED : CARD_RED} thud={stampThud} />}
        </article>
    );
};

// ---------------- The rail ---------------- //

const Chip = ({ number, status, pinned, active, onClick, chipRef }) => {
    const own = status === "own";
    return (
        <button
            ref={chipRef}
            type="button"
            onClick={onClick}
            aria-label={`No. ${cardLabel(number)}${own ? ", yours" : status === "cleared" ? ", filed" : status === "saving" ? ", saving" : ""}${pinned ? ", pinned" : ""}`}
            aria-current={active ? "true" : undefined}
            className="relative shrink-0 h-12 min-w-[48px] px-2 rounded-xl inline-flex flex-col items-center justify-center font-bold"
            style={{
                fontFamily: MONO,
                background: own ? "#E3DED3" : status === "cleared" ? `${CARD_RED}14` : "#FFFFFF",
                color: own ? MUTED : CARD_INK,
                border: `2px solid ${active ? CARD_INK : status === "cleared" ? CARD_RED : LINE}`,
            }}
        >
            <span className="text-sm leading-none">{cardLabel(number)}</span>
            <span className="text-[9px] leading-none mt-0.5 tracking-[0.06em]" style={{ fontFamily: SANS, color: status === "cleared" ? CARD_RED : MUTED }}>
                {own ? "You" : status === "cleared" ? "CLEARED" : status === "saving" ? "Saving…" : " "}
            </span>
            {status === "saving" && <Check size={10} className="absolute top-1 left-1" aria-hidden="true" />}
            {pinned && <Pin size={11} className="absolute top-1 right-1" aria-hidden="true" />}
        </button>
    );
};

// ---------------- The summary, after the reveal ---------------- //

/**
 * Every card's answer, once the reveal is finished: the card's number, who declared it, and (in brackets) which of
 * its statements was the lie, numbered as they appear on the card
 * @param {Array} cards     -   The deck's cards
 * @param {Object} results  -   { cards: { [number]: { passenger, lie, rightPassenger, rightLie } } }
 */
const Summary = ({ cards, results, onClose }) => (
    <div className="fixed inset-0 z-40 flex flex-col pt-[calc(env(safe-area-inset-top,0px)+16px)] pb-[calc(env(safe-area-inset-bottom,0px)+16px)]" style={{ background: CARD_PAPER }} role="dialog" aria-modal="true" aria-label="Summary">
        <div className="flex items-center justify-between gap-3 px-4 pb-3" style={{ borderBottom: `1px solid ${LINE}` }}>
            <div>
                <p className="text-[10px] font-bold tracking-[0.22em]" style={{ color: MUTED }}>UNCLAIMED DECLARATIONS · ANSWERS</p>
                <p className="text-lg font-bold">Who declared what</p>
                <p className="text-xs" style={{ color: MUTED }}>The number in brackets is the statement that was the lie</p>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="h-12 w-12 shrink-0 rounded-full inline-flex items-center justify-center" style={{ background: BACKDROP }}>
                <X size={20} aria-hidden="true" />
            </button>
        </div>
        <ol className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4">
            {cards.map((card) => {
                const answer = results.cards[card.number];
                if (!answer) return null;
                return (
                    <li key={card.number} className="flex items-center gap-3 py-3" style={{ borderBottom: `1px solid ${LINE}` }}>
                        <span className="text-xl font-bold w-12 shrink-0" style={{ fontFamily: MONO }}>{cardLabel(card.number)}</span>
                        <span className="h-3 w-3 rounded-full shrink-0" style={{ background: answer.passenger.colour }} aria-hidden="true" />
                        <span className="flex-1 min-w-0 text-base"><b className="font-semibold">{answer.passenger.name}</b> <span style={{ color: MUTED }}>· {answer.passenger.city}</span></span>
                        <span className="text-base font-bold shrink-0" style={{ fontFamily: MONO, color: CARD_RED }} aria-label={`The lie was statement ${answer.lie + 1}`}>({answer.lie + 1})</span>
                    </li>
                );
            })}
        </ol>
    </div>
);

// ---------------- The countdown ---------------- //

/**
 * Time to opening (7:00 PM IST), then time left to file, as HH:MM:SS (e.g. 20:32:07). Asks the page to refresh when
 * either reaches zero, so the deck opens or turns read-only (the server switches at the same moment).
 * @param {String} opensAt      -   When guessing opens
 * @param {String} closesAt     -   When guessing closes (11:30 PM IST, unless the host extends it)
 * @param {String} phase        -   The game's phase
 * @param {Function} onChange   -   Called once when time runs out
 */
const Countdown = ({ opensAt, closesAt, phase, onChange }) => {
    const firedRef = useRef(false);
    const open = phase === "open";
    const opening = phase === "locked" && !!opensAt;
    const target = open ? closesAt : opening ? opensAt : null;
    const left = useTimeLeft(target, open || opening);

    // A new time to count to (opened, or the host extended the timer): count down again
    useEffect(() => { firedRef.current = false; }, [target]);

    useEffect(() => {
        if ((open || opening) && left !== null && left <= 0 && !firedRef.current) {
            firedRef.current = true;
            onChange();
        }
    }, [open, opening, left, onChange]);

    let label = "CLOSES IN";
    let time;
    if (opening && left > 0) {
        label = "OPENS IN";
        time = formatHms(left);
    } else if (phase === "locked" || phase === "unlocked") {
        label = "IMMIGRATION";
        time = "Opens soon";
    } else if (!open || left === null || left <= 0) {
        label = "IMMIGRATION";
        time = "Closed";
    } else {
        time = formatHms(left);
    }
    const urgent = open && left !== null && left > 0 && left < 10 * 60 * 1000;
    return (
        <div className="flex flex-col items-end shrink-0 leading-none" role="timer" aria-live="off" aria-label={`${label.toLowerCase()} ${time}`}>
            <span className="text-[10px] font-bold tracking-[0.22em]" style={{ color: urgent ? CARD_RED : MUTED }}>{label}</span>
            <span className="text-xl font-bold mt-1" style={{ fontFamily: MONO, color: urgent ? CARD_RED : CARD_INK }}>{time}</span>
        </div>
    );
};

// ---------------- The page ---------------- //

const PHASE_BANNERS = {
    locked      :   { text: "Immigration opens soon", tone: CARD_INK },
    closed      :   { text: "IMMIGRATION CLOSED · Answers on landing.", tone: CARD_RED },
    revealing   :   { text: "THE REVEAL IS ON · Watch the TV", tone: OK },
    people      :   { text: "PASSENGERS REVEALED · Now find the lies", tone: OK },
};

// While guessing is closed (waiting for, or during, the reveal): checked often, so the phone keeps up with the TV
const REVEAL_POLL_MS = 3000;

const Deck = ({ pnr, initial, targetCard, onSignOut }) => {
    const [data, setData] = useState(initial);
    const [pending, setPendingState] = useState(() => readPending(pnr));
    const [drafts, setDrafts] = useState({});
    const [editing, setEditing] = useState({});
    const [filter, setFilter] = useState("all");
    const [active, setActive] = useState(0);
    const [scanning, setScanning] = useState(false);
    const [showSummary, setShowSummary] = useState(false);
    const [scanUnavailable, setScanUnavailable] = useState(() => !navigator.mediaDevices?.getUserMedia);
    const [fly, setFly] = useState(null);
    const [thud, setThud] = useState(null);
    const deckRef = useRef(null);
    const railRef = useRef(null);
    const cardRefs = useRef({});
    const chipRefs = useRef({});
    const pendingRef = useRef(pending);
    const jumpAfterRender = useRef(null);
    const timers = useRef([]);

    const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    useEffect(() => () => timers.current.forEach(clearTimeout), []);

    const setPending = useCallback((update) => {
        setPendingState((current) => {
            const next = typeof update === "function" ? update(current) : update;
            pendingRef.current = next;
            writePending(pnr, next);
            return next;
        });
    }, [pnr]);

    const { phase, me, cards, passengers, reports, pins } = data;
    const revealed = data.revealed || {};
    // Points so far in the reveal: the lie once shown, the passenger once shown
    const scoreSoFar = Object.values(revealed).reduce((sum, card) => sum + (card.rightLie ? 1 : 0) + (card.rightPassenger ? 1 : 0), 0);
    const passengersByPnr = useMemo(() => Object.fromEntries(passengers.map((p) => [p.pnr, p])), [passengers]);
    const status = (number) => (number === me.card ? "own" : pending[number] ? "saving" : reports[number] ? "cleared" : "empty");
    // Who this guest has already named, and on which card (saved or waiting to save)
    const named = useMemo(() => {
        const result = {};
        Object.entries({ ...reports, ...pending }).forEach(([number, pick]) => { if (pick?.suspectPnr) result[pick.suspectPnr] = Number(number); });
        return result;
    }, [reports, pending]);
    // The same, without the card itself, for each card's dropdown
    const usedOnFor = (number) => Object.fromEntries(Object.entries(named).filter(([, on]) => on !== number));
    const filedCount = cards.filter((card) => card.number !== me.card && (reports[card.number] || pending[card.number])).length;
    const toFile = cards.filter((card) => card.number !== me.card).length;

    const visible = useMemo(() => cards.filter((card) => {
        if (filter === "todo") return card.number !== me.card && !reports[card.number] && !pending[card.number];
        if (filter === "pinned") return pins.includes(card.number);
        return true;
    }), [cards, filter, me.card, reports, pending, pins]);

    // ---- Moving around the deck ----

    const scrollToCard = useCallback((number, smooth) => {
        const deck = deckRef.current;
        const element = cardRefs.current[number];
        if (!deck || !element) return;
        deck.scrollTo({ left: element.offsetLeft - (deck.clientWidth - element.clientWidth) / 2, behavior: smooth && !prefersReducedMotion() ? "smooth" : "auto" });
        const chip = chipRefs.current[number];
        const rail = railRef.current;
        if (chip && rail) rail.scrollTo({ left: chip.offsetLeft - rail.clientWidth / 2 + chip.clientWidth / 2, behavior: "smooth" });
    }, []);

    // Jump to a card. withFlyIn: the QR "fly in" (a printed card was scanned)
    const jumpTo = useCallback((number, withFlyIn = false) => {
        if (!cards.some((card) => card.number === number)) return false;
        if (!visible.some((card) => card.number === number)) {
            jumpAfterRender.current = { number, withFlyIn };
            setFilter("all");
            return true;
        }
        scrollToCard(number, !withFlyIn);
        if (withFlyIn) {
            setFly(null);
            requestAnimationFrame(() => setFly(number));
            later(() => setFly(null), 1700);
        }
        return true;
    }, [cards, visible, scrollToCard]);

    // After a filter change caused by a jump, finish the jump once the cards are on the page
    useLayoutEffect(() => {
        const queued = jumpAfterRender.current;
        if (queued && visible.some((card) => card.number === queued.number)) {
            jumpAfterRender.current = null;
            jumpTo(queued.number, queued.withFlyIn);
        }
    }, [visible, jumpTo]);

    // A printed card's QR: bring it to the front once
    useEffect(() => {
        if (targetCard) later(() => jumpTo(targetCard, true), 50);
        // Only on arrival
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Which card is in front, from the deck's scroll position
    useEffect(() => {
        const deck = deckRef.current;
        if (!deck) return undefined;
        let frame = 0;
        const onScroll = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const middle = deck.scrollLeft + deck.clientWidth / 2;
                let best = 0;
                let bestDistance = Infinity;
                visible.forEach((card, index) => {
                    const element = cardRefs.current[card.number];
                    if (!element) return;
                    const distance = Math.abs(element.offsetLeft + element.clientWidth / 2 - middle);
                    if (distance < bestDistance) { bestDistance = distance; best = index; }
                });
                setActive(best);
            });
        };
        onScroll();
        deck.addEventListener("scroll", onScroll, { passive: true });
        return () => { cancelAnimationFrame(frame); deck.removeEventListener("scroll", onScroll); };
    }, [visible]);

    // Arrow keys on a computer
    useEffect(() => {
        const onKey = (e) => {
            if (scanning || e.target instanceof HTMLInputElement) return;
            const next = e.key === "ArrowRight" ? active + 1 : e.key === "ArrowLeft" ? active - 1 : null;
            if (next === null || !visible[next]) return;
            e.preventDefault();
            jumpTo(visible[next].number);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [active, visible, scanning, jumpTo]);

    // ---- Keeping up to date: every 20 seconds, and after the phone wakes ----

    const refresh = useCallback(async () => {
        try {
            const fresh = await getImmigration(pnr);
            setData(fresh);
        } catch {
            // Offline for now: the next poll tries again
        }
    }, [pnr]);

    // ---- Saving picks: straight away, and retried until saved ----

    const savePending = useCallback(async () => {
        for (const [number, pick] of Object.entries(pendingRef.current)) {
            try {
                const saved = await fileReport(pnr, Number(number), pick.suspectPnr, pick.lie);
                setData((current) => ({ ...current, reports: { ...current.reports, [number]: { suspectPnr: saved.suspectPnr, lie: saved.lie, updatedAt: saved.updatedAt } } }));
                setPending((current) => {
                    const { [number]: done, ...rest } = current;
                    // Only drop it if it wasn't changed again in the meantime
                    return done && done.suspectPnr === pick.suspectPnr && done.lie === pick.lie ? rest : current;
                });
            } catch (err) {
                if (err.status && err.status < 500 && err.status !== 401 && err.status !== 429) {
                    // Refused for good (e.g. immigration closed): don't retry forever
                    setPending((current) => { const { [number]: dropped, ...rest } = current; return rest; });
                    toast.error(`Couldn't file No. ${cardLabel(number)}: ${err.message}`);
                }
                // Network trouble: kept, and retried
            }
        }
    }, [pnr, setPending]);

    // Follow the TV: each card it moves to comes to the front
    const followed = useRef(null);
    useEffect(() => {
        const number = data.revealCard;
        if (number && number !== followed.current) {
            followed.current = number;
            later(() => jumpTo(number), 50);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.revealCard]);

    const live = phase === "closed";
    useEffect(() => {
        const poll = setInterval(refresh, live ? REVEAL_POLL_MS : POLL_MS);
        const retry = setInterval(() => { if (Object.keys(pendingRef.current).length && navigator.onLine !== false) savePending(); }, RETRY_MS);
        const wake = () => { if (document.visibilityState === "visible") { refresh(); savePending(); } };
        document.addEventListener("visibilitychange", wake);
        window.addEventListener("online", wake);
        savePending();
        return () => {
            clearInterval(poll);
            clearInterval(retry);
            document.removeEventListener("visibilitychange", wake);
            window.removeEventListener("online", wake);
        };
    }, [refresh, savePending, live]);

    // ---- Filing ----

    const draftFor = (number) => drafts[number] ?? (pending[number] || reports[number] ? { ...(pending[number] || reports[number]) } : {});
    const setDraft = (number, change) => setDrafts((current) => ({ ...current, [number]: { ...draftFor(number), ...change } }));

    const file = (number) => {
        const pick = draftFor(number);
        setPending((current) => ({ ...current, [number]: { suspectPnr: pick.suspectPnr, lie: pick.lie } }));
        setEditing((current) => ({ ...current, [number]: false }));
        setThud(number);
        later(() => setThud(null), 700);
        navigator.vibrate?.(30);
        toast.success(`Report filed for No. ${cardLabel(number)}`, { id: `filed-${number}` });
        savePending();
        // On to the next card still to file, in this filter
        later(() => {
            const order = visible.map((card) => card.number);
            const from = order.indexOf(number);
            const next = [...order.slice(from + 1), ...order.slice(0, from)].find((n) => n !== me.card && !reports[n] && !pendingRef.current[n]);
            if (next) jumpTo(next);
        }, ADVANCE_MS);
    };

    const togglePin = async (number) => {
        const pinned = pins.includes(number);
        const update = (on) => setData((current) => ({ ...current, pins: on ? [...current.pins, number] : current.pins.filter((n) => n !== number) }));
        update(!pinned);
        try {
            await setPin(pnr, number, !pinned);
        } catch {
            update(pinned);
            toast.error("Couldn't save the pin. Try again.");
        }
    };

    // ---- Scanning a printed card ----

    const handleScan = (code) => {
        let number = null;
        try {
            number = parseInt(new URL(code).searchParams.get("card"), 10);
        } catch {
            number = /^\d{1,3}$/.test(code.trim()) ? parseInt(code, 10) : null;
        }
        if (number && jumpTo(number, true)) {
            setScanning(false);
            navigator.vibrate?.(30);
        } else {
            toast.error("That's not a declaration card.", { id: "scan" });
        }
    };

    const banner = phase === "locked" && data.opensAt ? { ...PHASE_BANNERS.locked, text: `Immigration opens at ${formatTime(data.opensAt)}` }
        : PHASE_BANNERS[phase === "closed" && data.revealStarted ? "revealing" : phase === "open" && data.peopleRevealed ? "people" : phase];
    return (
        <>
            <header className="imm-header shrink-0 flex items-center justify-between gap-3" style={{ background: CARD_PAPER, borderBottom: `1px solid ${LINE}` }}>
                <div className="min-w-0">
                    <p className="text-[10px] font-bold tracking-[0.22em] truncate" style={{ color: MUTED }}>RIGHT BROTHER AIRWAYS · IMMIGRATION</p>
                    <p className="text-base font-bold leading-tight">
                        {phase === "finished" && data.results ? `Your score: ${data.results.score} ${data.results.score === 1 ? "pt" : "pts"}`
                            : data.revealStarted ? `So far: ${scoreSoFar} ${scoreSoFar === 1 ? "pt" : "pts"}` : `${filedCount} of ${toFile} filed`}
                        {phase === "finished" && data.results && (
                            <button type="button" onClick={() => setShowSummary(true)} className="ml-2 align-middle text-xs font-bold tracking-[0.12em] underline underline-offset-4">SHOW SUMMARY</button>
                        )}
                    </p>
                    <p className="text-xs truncate" style={{ color: MUTED }}>
                        Filing as {me.firstName} ·{" "}
                        <button type="button" onClick={onSignOut} className="underline underline-offset-2 min-h-[24px]">Not you?</button>
                    </p>
                </div>
                <Countdown opensAt={data.opensAt} closesAt={data.closesAt} phase={phase} onChange={refresh} />
            </header>

            {banner && <p className="shrink-0 px-4 py-2 text-center text-sm font-bold tracking-[0.14em]" style={{ background: banner.tone, color: CARD_PAPER }}>{banner.text}</p>}
            {scanUnavailable && phase !== "unlocked" && phase !== "finished" && (
                <p className="shrink-0 px-4 py-1.5 text-center text-xs" style={{ color: MUTED }}>To scan a card, use your phone's camera app.</p>
            )}

            {phase === "unlocked" ? (
                <div className="flex-1 flex items-center justify-center px-8 text-center">
                    <p className="text-lg">The declaration cards are being printed. Check back once you've landed.</p>
                </div>
            ) : visible.length === 0 ? (
                <div className="flex-1 flex items-center justify-center px-8 text-center">
                    <p className="text-lg" style={{ color: MUTED }}>
                        {filter === "todo" ? "All filed. Nice work, officer." : "No pinned cards yet. Tap the pin on a card to keep it here."}
                    </p>
                </div>
            ) : (
                <div ref={deckRef} className="imm-deck" aria-label="Declaration cards">
                    {visible.map((card) => (
                        <Card
                            key={card.number}
                            cardRef={(element) => { cardRefs.current[card.number] = element; }}
                            card={card}
                            phase={phase}
                            own={card.number === me.card}
                            report={reports[card.number]}
                            pending={pending[card.number]}
                            pinned={pins.includes(card.number)}
                            draft={draftFor(card.number)}
                            editing={!!editing[card.number]}
                            passengers={passengers}
                            passengersByPnr={passengersByPnr}
                            usedOn={usedOnFor(card.number)}
                            result={revealed[card.number]}
                            peopleRevealed={!!data.peopleRevealed}
                            fly={fly === card.number}
                            stampThud={thud === card.number}
                            onPassenger={(change) => setDraft(card.number, change)}
                            onPickLie={(index) => setDraft(card.number, { lie: draftFor(card.number).lie === index ? null : index })}
                            onFile={() => file(card.number)}
                            onAmend={() => setEditing((current) => ({ ...current, [card.number]: true }))}
                            onTogglePin={() => togglePin(card.number)}
                        />
                    ))}
                </div>
            )}

            {phase !== "unlocked" && (
                <nav className="imm-rail shrink-0" aria-label="Jump to a card">
                    <div className="flex gap-1.5 pb-1.5 shrink-0" role="tablist" aria-label="Filter cards">
                        {FILTERS.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                role="tab"
                                aria-selected={filter === item.id}
                                onClick={() => setFilter(item.id)}
                                className="min-h-[36px] px-2.5 min-[360px]:px-3 whitespace-nowrap rounded-full text-xs font-bold tracking-[0.06em] min-[360px]:tracking-[0.1em]"
                                style={filter === item.id ? { background: CARD_INK, color: CARD_PAPER } : { background: BACKDROP, color: CARD_INK }}
                            >
                                {item.label}
                            </button>
                        ))}
                        {!scanUnavailable && (
                            <button type="button" onClick={() => setScanning(true)} aria-label="Scan a card's QR code" className="ml-auto h-9 w-9 shrink-0 rounded-full inline-flex items-center justify-center" style={{ background: CARD_INK, color: CARD_PAPER }}>
                                <ScanLine size={18} aria-hidden="true" />
                            </button>
                        )}
                    </div>
                    <div ref={railRef} className="imm-chips flex-1 min-w-0">
                        {visible.map((card, index) => (
                            <Chip
                                key={card.number}
                                chipRef={(element) => { chipRefs.current[card.number] = element; }}
                                number={card.number}
                                status={status(card.number)}
                                pinned={pins.includes(card.number)}
                                active={index === active}
                                onClick={() => jumpTo(card.number)}
                            />
                        ))}
                    </div>
                </nav>
            )}

            {showSummary && data.results && <Summary cards={cards} results={data.results} onClose={() => setShowSummary(false)} />}

            {scanning && (
                <div className="fixed inset-0 z-40 flex flex-col p-4 pt-[calc(env(safe-area-inset-top,0px)+16px)] pb-[calc(env(safe-area-inset-bottom,0px)+16px)]" style={{ background: CARD_PAPER }} role="dialog" aria-modal="true" aria-label="Scan a card">
                    <div className="flex items-center justify-between pb-3">
                        <p className="text-lg font-bold">Scan a card on the wall</p>
                        <button type="button" onClick={() => setScanning(false)} aria-label="Close" className="h-12 w-12 rounded-full inline-flex items-center justify-center" style={{ background: BACKDROP }}>
                            <X size={20} aria-hidden="true" />
                        </button>
                    </div>
                    <QrScanner
                        autoStart
                        onCode={handleScan}
                        onError={() => {
                            setScanning(false);
                            setScanUnavailable(true);
                            toast("Camera unavailable. Use your phone's camera app to scan the card instead.", { id: "scan", duration: 6000 });
                        }}
                    />
                </div>
            )}
        </>
    );
};

const ImmigrationPage = () => {
    const [searchParams] = useSearchParams();
    const targetCard = parseInt(searchParams.get("card"), 10) || null;
    // 'loading' | 'signin' | 'not_attending' | 'error' | 'ready'
    const [state, setState] = useState("loading");
    const [pnr, setPnr] = useState(null);
    const [initial, setInitial] = useState(null);

    useNoIndex("Immigration · Flight YJ030");

    // While this page is open: edge to edge on notched phones (the header and rail pad themselves with the safe areas),
    // and pinch-zoom allowed for accessibility (the rest of the site turns it off). Buttons still don't double-tap zoom.
    useEffect(() => {
        const meta = document.querySelector('meta[name="viewport"]');
        if (!meta) return undefined;
        const previous = meta.getAttribute("content");
        meta.setAttribute("content", "width=device-width, initial-scale=1, viewport-fit=cover");
        return () => meta.setAttribute("content", previous);
    }, []);

    const load = useCallback(async (chosen) => {
        setState("loading");
        try {
            let player = chosen;
            if (!player) {
                const { pnrs } = await getCheckInSession();
                player = pnrs[pnrs.length - 1];
            }
            if (!player) {
                setState("signin");
                return;
            }
            const data = await getImmigration(player);
            setPnr(player);
            setInitial(data);
            setState("ready");
        } catch (err) {
            if (err.status === 401) setState("signin");
            else if (err.status === 403) setState("not_attending");
            else setState("error");
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="yj-imm">
            <style>{STYLES}</style>
            {state === "loading" && (
                <div className="flex-1 flex items-center justify-center">
                    <p className="text-sm font-bold tracking-[0.24em] animate-pulse" style={{ color: MUTED }}>CHECKING YOUR PASSPORT…</p>
                </div>
            )}
            {state === "signin" && (
                <>
                    <header className="imm-header" style={{ background: CARD_PAPER, borderBottom: `1px solid ${LINE}` }}>
                        <p className="text-[10px] font-bold tracking-[0.22em]" style={{ color: MUTED }}>RIGHT BROTHER AIRWAYS · IMMIGRATION</p>
                        <p className="text-base font-bold">Unclaimed Declarations</p>
                    </header>
                    <SignIn onSignedIn={(signedIn) => load(signedIn)} />
                </>
            )}
            {state === "not_attending" && (
                <div className="flex-1 flex flex-col items-center justify-center gap-3 px-8 text-center">
                    <p className="text-xl font-bold">You're not on this flight's immigration list.</p>
                    <p style={{ color: MUTED }}>The guessing game is for passengers at the party.</p>
                    <button type="button" onClick={() => setState("signin")} className="min-h-[44px] text-sm underline underline-offset-2">Use a different passport</button>
                </div>
            )}
            {state === "error" && (
                <div className="flex-1 flex flex-col items-center justify-center gap-4 px-8 text-center">
                    <p className="text-lg font-bold">We couldn't reach immigration.</p>
                    <button type="button" onClick={() => load(pnr)} className="min-h-[48px] rounded-full px-6 text-sm font-bold tracking-[0.16em]" style={{ background: CARD_INK, color: CARD_PAPER }}>TRY AGAIN</button>
                </div>
            )}
            {state === "ready" && <Deck key={pnr} pnr={pnr} initial={initial} targetCard={targetCard} onSignOut={() => setState("signin")} />}
        </div>
    );
};

export default ImmigrationPage;
